// src/components/social/ConversationList.jsx
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageContext } from '../../context/messageContext';
import ReactTimeAgo from 'react-time-ago';
import Avatar from '../ui/Avatar';
import Loader from '../common/Loader';
import { FaSearch, FaComment } from 'react-icons/fa';

const ConversationList = () => {
  const { conversations, activeConversation, loading, fetchConversations } = useContext(MessageContext);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [fetchAttempted, setFetchAttempted] = useState(false);

  // Fetch conversations on mount only once
  useEffect(() => {
    const getConversations = async () => {
      if (!fetchAttempted) {
        await fetchConversations();
        setFetchAttempted(true);
      }
    };
    
    getConversations();
  }, [fetchConversations, fetchAttempted]);

  // Filter conversations by search term
  const filteredConversations = conversations.filter(
    convo => convo.participant.name.toLowerCase().includes(search.toLowerCase())
  );

  // Format last message preview
  const formatMessagePreview = (message) => {
    if (!message) return 'No messages yet';

    if (message.media) {
      return '📷 Photo';
    }

    // Truncate long messages
    return message.content.length > 30 
      ? `${message.content.substring(0, 30)}...` 
      : message.content;
  };

  // Handle conversation selection
  const handleSelectConversation = (conversationId) => {
    navigate(`/messages/${conversationId}`);
  };

  // Show loading state only on initial load
  if (loading && conversations.length === 0 && !fetchAttempted) {
    return (
      <div className="flex justify-center items-center h-full py-8">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {filteredConversations.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-4 text-gray-500 text-center">
          <FaComment className="text-gray-300 mb-2" size={32} />
          {search 
            ? 'No conversations matching your search'
            : 'No conversations yet'
          }
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.map(convo => (
            <div 
              key={convo._id} 
              className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 ${
                activeConversation === convo._id ? 'bg-blue-50' : ''
              }`}
              onClick={() => handleSelectConversation(convo._id)}
            >
              <div className="relative flex-shrink-0">
                <Avatar 
                  user={convo.participant} 
                  size="md" 
                  linkToProfile={false} 
                  className="w-12 h-12 rounded-full"
                />
                {convo.participant.isOnline && (
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                )}
              </div>
              
              <div className="ml-3 flex-1 min-w-0">
                <div className="flex justify-between">
                  <h4 className="text-sm font-medium text-gray-900 truncate">
                    {convo.participant?.name || "User"}
                  </h4>
                  <span className="text-xs text-gray-500">
                    {convo.lastMessage ? (
                      <ReactTimeAgo 
                        date={new Date(convo.lastMessage.createdAt)} 
                        timeStyle="twitter"
                      />
                    ) : null}
                  </span>
                </div>
                
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-gray-500 truncate">
                    {convo.lastMessage ? 
                      formatMessagePreview(convo.lastMessage) : 
                      'Start a conversation'
                    }
                  </p>
                  
                  {convo.unreadCount > 0 && (
                    <span className="ml-2 flex-shrink-0 inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-primary text-white">
                      {convo.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConversationList;